import { Reducer } from 'react';

import {
  Action,
  IAdminGroup,
  IAdminVacanciesPayload,
  ICompactVacancy,
  IVacancyFullInfo,
  IVacancyGroupPayload,
  IVacancyPayload,
  IVacancyRecommendationPayload,
  IVacancyResponsePayload,
} from '../../utils/types/state-types';
import {
  ADMIN_VACANCIES_RECEIVED,
  ADMIN_VACANCIES_REJECTED,
  ADMIN_VACANCIES_REQUESTED,
  CREATE_VACANCY_GROUP_RECEIVED,
  CREATE_VACANCY_GROUP_REJECTED,
  CREATE_VACANCY_GROUP_REQUESTED,
  DELETE_VACANCY_GROUP_RECEIVED,
  DELETE_VACANCY_GROUP_REJECTED,
  DELETE_VACANCY_GROUP_REQUESTED,
  DELETE_VACANCY_RECEIVED,
  DELETE_VACANCY_REJECTED,
  DELETE_VACANCY_REQUESTED,
  EDIT_VACANCY_GROUP_RECEIVED,
  EDIT_VACANCY_GROUP_REJECTED,
  EDIT_VACANCY_GROUP_REQUESTED,
  EDIT_VACANCY_RECEIVED,
  EDIT_VACANCY_REJECTED,
  EDIT_VACANCY_REQUESTED,
  OTHERS_VACANCIES_RECEIVED,
  OTHERS_VACANCIES_REJECTED,
  OTHERS_VACANCIES_REQUESTED,
  VACANCY_GROUPS_RECEIVED,
  VACANCY_GROUPS_REJECTED,
  VACANCY_GROUPS_REQUESTED,
} from '../actions/admin-page';
import {
  ERROR_RESET,
  POST_VACANCY_RECOMMENDATION_RECEIVED,
  POST_VACANCY_RECOMMENDATION_REJECTED,
  POST_VACANCY_RECOMMENDATION_REQUESTED,
  POST_VACANCY_RESPONSE_RECEIVED,
  POST_VACANCY_RESPONSE_REJECTED,
  POST_VACANCY_RESPONSE_REQUESTED,
  VACANCY_RECEIVED,
  VACANCY_REJECTED,
  VACANCY_REQUESTED,
  VACANCY_RESET,
} from '../actions/vacancy-page';

export interface IVacancyReducerState {
  error?: string;
  vacancy: IVacancyFullInfo | null;
  responseSent: boolean;
  recommendationSent: boolean;
  responseError?: string;
  recommendationError?: string;
  isLoading: boolean;
  isSending: boolean;
  groups: IAdminGroup[];
  groupVacancies: ICompactVacancy[];
  otherVacancies: ICompactVacancy[];
  isListLoading: boolean;
}

const initialState = {
  vacancy: null,
  error: '',
  responseSent: false,
  recommendationSent: false,
  responseError: '',
  recommendationError: '',
  isLoading: false,
  isSending: false,
  groups: [],
  groupVacancies: [],
  otherVacancies: [],
  isListLoading: false,
} as IVacancyReducerState;

const vacancyReducer: Reducer<IVacancyReducerState, Action> = (
  state = initialState,
  action: Action,
) => {
  const { type } = action;
  switch (type) {
    case VACANCY_REQUESTED: {
      return {
        ...state,
        isLoading: true,
        error: '',
      };
    }
    case VACANCY_RECEIVED: {
      const { payload } = action as Action<IVacancyPayload>;
      return {
        ...state,
        isLoading: false,
        vacancy: payload.vacancy,
        error: '',
      };
    }
    case VACANCY_REJECTED: {
      const { payload } = action as Action<IVacancyPayload>;
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    }
    case VACANCY_RESET: {
      return {
        ...state,
        vacancy: null,
      };
    }
    case POST_VACANCY_RESPONSE_REQUESTED: {
      return {
        ...state,
        isSending: true,
        error: '',
      };
    }
    case POST_VACANCY_RESPONSE_RECEIVED: {
      return {
        ...state,
        responseSent: true,
        isSending: false,
        error: '',
      };
    }
    case POST_VACANCY_RESPONSE_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        responseSent: false,
        isSending: false,
        responseError: payload.error,
      };
    }
    case POST_VACANCY_RECOMMENDATION_REQUESTED: {
      return {
        ...state,
        isSending: true,
        error: '',
      };
    }
    case POST_VACANCY_RECOMMENDATION_RECEIVED: {
      return {
        ...state,
        recommendationSent: true,
        isSending: false,
        error: '',
      };
    }
    case POST_VACANCY_RECOMMENDATION_REJECTED: {
      const { payload } = action as Action<IVacancyRecommendationPayload>;
      return {
        ...state,
        isSending: false,
        recommendationSent: false,
        recommendationError: payload.error,
      };
    }
    case VACANCY_GROUPS_REQUESTED: {
      return {
        ...state,
        isLoading: true,
        error: '',
      };
    }
    case VACANCY_GROUPS_REJECTED: {
      return {
        ...state,
        isLoading: false,
        error: '',
      };
    }
    case VACANCY_GROUPS_RECEIVED: {
      const { payload } = action as Action<IVacancyGroupPayload>;
      return {
        ...state,
        isLoading: false,
        groups: payload.groups,
      };
    }
    case CREATE_VACANCY_GROUP_REQUESTED: {
      return {
        ...state,
        isLoading: true,
        error: '',
      };
    }
    case CREATE_VACANCY_GROUP_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    }
    case CREATE_VACANCY_GROUP_RECEIVED: {
      return {
        ...state,
        isLoading: false,
        error: '',
      };
    }
    case EDIT_VACANCY_GROUP_REQUESTED: {
      return {
        ...state,
        isLoading: true,
        error: '',
      };
    }
    case EDIT_VACANCY_GROUP_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    }
    case EDIT_VACANCY_GROUP_RECEIVED: {
      return {
        ...state,
        isLoading: false,
        error: '',
      };
    }
    case ADMIN_VACANCIES_REQUESTED: {
      return {
        ...state,
        isLoading: true,
        error: '',
      };
    }
    case ADMIN_VACANCIES_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    }
    case ADMIN_VACANCIES_RECEIVED: {
      const { payload } = action as Action<IAdminVacanciesPayload>;
      return {
        ...state,
        isLoading: false,
        error: '',
        groupVacancies: payload.groupVacancies,
      };
    }
    case OTHERS_VACANCIES_REQUESTED: {
      return {
        ...state,
        isListLoading: true,
        error: '',
      };
    }
    case OTHERS_VACANCIES_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isListLoading: false,
        error: payload.error,
      };
    }
    case OTHERS_VACANCIES_RECEIVED: {
      const { payload } = action as Action<IAdminVacanciesPayload>;
      return {
        ...state,
        isListLoading: false,
        error: '',
        otherVacancies: payload.groupVacancies,
      };
    }
    case EDIT_VACANCY_REQUESTED: {
      return {
        ...state,
        isListLoading: true,
        error: '',
      };
    }
    case EDIT_VACANCY_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isListLoading: false,
        error: payload.error,
      };
    }
    case EDIT_VACANCY_RECEIVED: {
      return {
        ...state,
        isListLoading: false,
        error: '',
      };
    }
    case DELETE_VACANCY_GROUP_REQUESTED: {
      return {
        ...state,
        isListLoading: true,
        error: '',
      };
    }
    case DELETE_VACANCY_GROUP_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    }
    case DELETE_VACANCY_GROUP_RECEIVED: {
      return {
        ...state,
        isLoading: false,
        error: '',
      };
    }
    case DELETE_VACANCY_REQUESTED: {
      return {
        ...state,
        isListLoading: true,
        error: '',
      };
    }
    case DELETE_VACANCY_REJECTED: {
      const { payload } = action as Action<IVacancyResponsePayload>;
      return {
        ...state,
        isLoading: false,
        error: payload.error,
      };
    }
    case DELETE_VACANCY_RECEIVED: {
      return {
        ...state,
        isLoading: false,
        error: '',
      };
    }
    case ERROR_RESET: {
      return {
        ...state,
        isLoading: false,
        error: '',
        responseError: '',
        responseSent: false,
        recommendationError: '',
        recommendationSent: false,
      };
    }
    default:
      return state;
  }
};

export default vacancyReducer;
